// Full Calendar Plugins
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";

// Notification
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

// eslint-disable-next-line object-curly-newline
import { ref, computed, watch, onMounted } from "@vue/composition-api";
import store from "@/store";

export default function userCalendar() {
  // Use toast
  const toast = useToast();
  // ------------------------------------------------
  // refCalendar
  // ------------------------------------------------
  const refCalendar = ref(null);

  // ------------------------------------------------
  // calendarApi
  // ------------------------------------------------
  let calendarApi = null;
  onMounted(() => {
    calendarApi = refCalendar.value.getApi();
  });

  // ------------------------------------------------
  // calendars
  // ------------------------------------------------
  const calendarsColor = {
    Students: "primary",
    Holiday: "success",
    Staff: "danger",
    Parents: "primary", // map Parents to Students color
    ETC: "info",
  };

  // ------------------------------------------------
  // event
  // ------------------------------------------------
  const blankEvent = {
    title: "",
    start: "",
    end: "",
    allDay: false,
    url: "",
    extendedProps: {
      calendar: [], // Changed to array to support multiple selections
      eventType: "event", // Set default to event to satisfy validation
      guests: [],
      location: "",
      description: "",
      classID: [0],
      secID: [0],
      departmentID: [],
      startTime: "",
      endTime: "",
      eventLink: "",
      contactPhone: "",
      attachments: [],
      uploadToAllTeachers: false,
    },
  };
  const event = ref(JSON.parse(JSON.stringify(blankEvent)));
  const clearEventData = () => {
    event.value = JSON.parse(JSON.stringify(blankEvent));
  };

  // *===========================================================================---*
  // *--------- Calendar API Function/Utils --------------------------------------------*
  // Template Future Update: We might move this utils function in its own file
  // *===========================================================================---*

  // ------------------------------------------------
  // (UI) updateEventInCalendar
  // ------------------------------------------------
  const updateEventInCalendar = (
    updatedEventData,
    propsToUpdate,
    extendedPropsToUpdate
  ) => {
    toast({
      component: ToastificationContent,
      props: {
        title: "Event Updated",
        icon: "CheckIcon",
        variant: "success",
      },
    });

    const existingEvent = calendarApi.getEventById(updatedEventData.id);

    // --- Set event properties except date related ----- //
    // ? Docs: https://fullcalendar.io/docs/Event-setProp
    // dateRelatedProps => ['start', 'end', 'allDay']
    // eslint-disable-next-line no-plusplus
    for (let index = 0; index < propsToUpdate.length; index++) {
      const propName = propsToUpdate[index];
      existingEvent.setProp(propName, updatedEventData[propName]);
    }

    // --- Set date related props ----- //
    // ? Docs: https://fullcalendar.io/docs/Event-setDates
    existingEvent.setDates(updatedEventData.start, updatedEventData.end, {
      allDay: updatedEventData.allDay,
    });

    // --- Set event's extendedProps ----- //
    // ? Docs: https://fullcalendar.io/docs/Event-setExtendedProp
    // eslint-disable-next-line no-plusplus
    for (let index = 0; index < extendedPropsToUpdate.length; index++) {
      const propName = extendedPropsToUpdate[index];
      existingEvent.setExtendedProp(
        propName,
        updatedEventData.extendedProps[propName]
      );
    }
  };

  // ------------------------------------------------
  // (UI) removeEventInCalendar
  // ------------------------------------------------
  const removeEventInCalendar = (eventId) => {
    toast({
      component: ToastificationContent,
      props: {
        title: "Event Removed",
        icon: "TrashIcon",
        variant: "danger",
      },
    });
    calendarApi.getEventById(eventId).remove();
  };

  // ------------------------------------------------
  // grabEventDataFromEventApi
  // ------------------------------------------------
  const grabEventDataFromEventApi = (eventApi) => {
    const { id, title, start, end, allDay, extendedProps = {} } = eventApi;

    const formatDateOnly = (date) => {
      if (!date) return "";
      if (typeof date === "string") return date.split("T")[0];
      return date.toISOString().split("T")[0];
    };

    let {
      calendar,
      guests = [],
      location = "",
      description = "",
      classID = [0],
      secID = [0],
      departmentID = [0],
      startTime = "",
      endTime = "",
      eventLink = "",
      contactPhone = "",
      attachments = [],
      // Map persisted values for edit mode
      eventType = eventApi.extendedProps?.eventType || eventApi.extendedProps?.type || "",
      uploadToAllTeachers = Boolean(
        eventApi.extendedProps?.uploadToAllTeachers !== undefined
          ? eventApi.extendedProps.uploadToAllTeachers
          : eventApi.extendedProps?.adjustPlanner
      ),
    } = extendedProps;

    // Convert calendar to array if it's a string (for backward compatibility)
    if (typeof calendar === "string") {
      // If it's a comma-separated string, split it
      if (calendar.includes(",")) {
        calendar = calendar.split(",").map(c => c.trim()).filter(c => c);
      } else {
        // Single value, convert to array
        calendar = calendar ? [calendar] : [];
      }
    } else if (!Array.isArray(calendar)) {
      calendar = [];
    }

    const eventData = {
      id,
      title,
      start: formatDateOnly(start),
      end: formatDateOnly(end),
      allDay,
      extendedProps: {
        calendar,
        guests,
        location,
        description,
        classID,
        secID,
        departmentID,
        startTime,
        endTime,
        eventLink,
        contactPhone,
        attachments,
        // Ensure editor reflects stored values
        eventType,
        uploadToAllTeachers,
      },
    };
    return eventData;
  };

  // ------------------------------------------------
  // addEvent
  // ------------------------------------------------
  const addEvent = (eventData) => {
    store.dispatch("calendar/addEvent", { event: eventData }).then(() => {
      refetchEvents();
    });
  };

  // ------------------------------------------------
  // updateEvent
  // ------------------------------------------------
  const updateEvent = (eventData) => {
    store
      .dispatch("calendar/updateEvent", { event: eventData })
      .then((response) => {
        const updatedEvent = response.data.event;

        const propsToUpdate = ["id", "title", "url"];
        const extendedPropsToUpdate = [
          "calendar",
          "guests",
          "location",
          "description",
          "classID",
          "secID",
        ];

        updateEventInCalendar(
          updatedEvent,
          propsToUpdate,
          extendedPropsToUpdate
        );
      });
  };

  // ------------------------------------------------
  // removeEvent
  // ------------------------------------------------
  const removeEvent = () => {
    const eventId = event.value.id;
    
    store.dispatch("calendar/removeEvent", { id: eventId, context: toast })
      .then((response) => {
        if (!response.cancelled) {
          if (response.success) {
            calendarApi.getEventById(eventId).remove();
            toast({
              component: ToastificationContent,
              props: {
                title: 'Event Deleted',
                text: `Event has been deleted successfully`,
                icon: 'CheckIcon',
                variant: 'success',
              },
            });
          }
        }
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Delete Failed',
            text: 'Failed to delete event. Please try again.',
            icon: 'XIcon',
            variant: 'danger',
          },
        });
      });
  };

  // ------------------------------------------------
  // isCalendarBusy (Loading State)
  // ------------------------------------------------
  const isCalendarBusy = ref(false);

  // ------------------------------------------------
  // refetchEvents
  // ------------------------------------------------
  const refetchEvents = () => {
    calendarApi.refetchEvents();
  };

  // ------------------------------------------------
  // selectedCalendars
  // ------------------------------------------------
  const selectedCalendars = computed(
    () => store.state.calendar.selectedCalendars
  );

  const showBoth = computed(
    () => store.state.calendar.showBoth
  );

  watch(selectedCalendars, () => {
    refetchEvents();
  }, { deep: true });

  watch(showBoth, () => {
    refetchEvents();
  });

  // --------------------------------------------------------------------------------------------------
  // AXIOS: fetchEvents
  // --------------------------------------------------------------------------------------------------
  const fetchEvents = (info, successCallback) => {
    if (!info) return;

    // Determine the year to load events for.
    // FullCalendar month view ranges always contain the full month being displayed.
    // By taking the date 15 days after the start of the visible range, we are 
    // guaranteed to be inside the center month of that view.
    let currentYear;
    
    if (info.start) {
      const startDate = new Date(info.start);
      // Move forward 15 days to reach the heart of the current view
      const midDate = new Date(startDate.getTime() + 15 * 24 * 60 * 60 * 1000);
      currentYear = midDate.getFullYear();
    }
    
    // Fallback if the above logic somehow fails (e.g. invalid date)
    if (!currentYear && info.view && info.view.currentStart) {
      currentYear = info.view.currentStart.getFullYear();
    }
    
    // Ultimate fallback to current year
    if (!currentYear) {
      currentYear = new Date().getFullYear();
    }

    const requestData = {
      year: currentYear,
      months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    };

    const obj = {
      url:
        store.state.domain +
        "EventsCalendar/Load?db=" +
        store.state.userData.db +
        "&cID=" +
        store.state.userData.cId,
      body: requestData,
      token: store.state.userData.token,
      allResponse: true,
      ignoreMessage: true,
    };

    isCalendarBusy.value = true;
    store
      .dispatch("postObj", obj)
      .then((response) => {
        isCalendarBusy.value = false;
        let eventsData = null;
        if (
          response?.data?.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          eventsData = response.data.data;
        } else if (Array.isArray(response?.data)) {
          eventsData = response.data;
        } else if (Array.isArray(response)) {
          eventsData = response;
        }

        if (eventsData && Array.isArray(eventsData) && eventsData.length > 0) {
          const transformedEvents = eventsData
            .map((item, index) => {
              let event = item.events || item.event || item;
              if (!event || !event.title) return null;

              // Map Parents to Students for visuals and filtering
              // Handle comma-separated eventFor values (e.g., "Staff,Students")
              let eventForRaw = event.eventFor || event.calendar || "Staff";
              
              // If it's a comma-separated string, convert to array
              let eventForArray = [];
              if (typeof eventForRaw === "string" && eventForRaw.includes(",")) {
                eventForArray = eventForRaw.split(",").map(e => e.trim());
                // Replace Parents with Students
                eventForArray = eventForArray.map(e => e === 'Parents' ? 'Students' : e);
              } else if (typeof eventForRaw === "string") {
                eventForArray = [eventForRaw === 'Parents' ? 'Students' : eventForRaw];
              } else if (Array.isArray(eventForRaw)) {
                eventForArray = eventForRaw.map(e => e === 'Parents' ? 'Students' : e);
              } else {
                eventForArray = ["Staff"];
              }
              
              // For display, use the first one or "Both" if contains both
              const eventFor = eventForArray.length > 1 && 
                               eventForArray.includes("Staff") && 
                               eventForArray.includes("Students")
                ? "Both"
                : eventForArray[0] || "Staff";

              const getEventColors = (type) => {
                switch (type) {
                  case "Students":
                    return {
                      backgroundColor: "rgba(115, 103, 240, 0.08)",
                      borderColor: "#7367f0",
                      textColor: "#7367f0",
                    };
                  case "Staff":
                    return {
                      backgroundColor: "rgba(234, 84, 85, 0.08)",
                      borderColor: "#ea5455",
                      textColor: "#ea5455",
                    };
                  case "Both":
                    return {
                      backgroundColor: "rgba(0, 207, 232, 0.08)",
                      borderColor: "#00cfe8",
                      textColor: "#00cfe8",
                    };
                  case "Holiday":
                    return {
                      backgroundColor: "rgba(40, 199, 111, 0.08)",
                      borderColor: "#28c76f",
                      textColor: "#28c76f",
                    };
                  default:
                    return {
                      backgroundColor: "#f8f9fa",
                      borderColor: "#6c757d",
                      textColor: "#495057",
                    };
                }
              };

              // Use "Both" color if event is for both Staff and Students
              const displayType = eventForArray.length > 1 && 
                                 eventForArray.includes("Staff") && 
                                 eventForArray.includes("Students")
                ? "Both"
                : eventFor;
              
              const colors = getEventColors(displayType);

              let startDate = event.start || event.startDate;
              let endDate = event.end || event.endDate;
              let isAllDay = event.allDay || false;

              if (isAllDay && endDate && startDate !== endDate) {
                const adjustedEndDate = new Date(endDate);
                adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
                endDate = adjustedEndDate.toISOString().split("T")[0];
              }

              return {
                id: event.id || `event-${index}`,
                title: event.title,
                start: startDate,
                end: endDate,
                allDay: isAllDay,
                backgroundColor: colors.backgroundColor,
                borderColor: colors.borderColor,
                textColor: colors.textColor,
                extendedProps: {
                  calendar: eventForArray, // Store as array for proper filtering
                  calendarDisplay: eventFor, // For display purposes
                  description: event.description || "",
                  location: event.location || "",
                  eventLink: event.locationLink || event.eventLink || "",
                  startTime: event.startTime || "",
                  endTime: event.endTime || "",
                  // Ensure edit sidebar gets correct type/adjust values
                  eventType: event.eventType || event.type || (eventFor === 'Holiday' || eventFor === 'holiday' ? 'holiday' : 'event'),
                  uploadToAllTeachers: (event.uploadToAllTeachers !== undefined ? event.uploadToAllTeachers : (event.adjustPlanner !== undefined ? event.adjustPlanner : false)),
                  classID: event.clsIDs
                    ? event.clsIDs.split(",").map(Number).filter(Boolean)
                    : [0],
                  secID: event.secIDs
                    ? event.secIDs.split(",").map(Number).filter(Boolean)
                    : [0],
                  departmentID: event.departmentIDs
                    ? event.departmentIDs.split(",").map(Number).filter(Boolean)
                    : [0],
                  contactPhone: event.contactPhone || "",
                  attachments: item.attachments || [],
                },
              };
            })
            .filter((event) => event !== null);

          // Get showBoth state from store
          const showBoth = store.state.calendar.showBoth || false;
          
          const filteredEvents = transformedEvents.filter((event) => {
            const eventCalendar = event.extendedProps.calendar; // This is now an array
            
            // Check if event is for both Staff and Students
            const isBothEvent = Array.isArray(eventCalendar) 
              ? eventCalendar.includes("Staff") && eventCalendar.includes("Students")
              : false;
            
            // If "Both" filter is enabled and this is a "both" event, show it
            if (showBoth && isBothEvent) {
              return true;
            }
            
            // Otherwise, use normal filtering
            // Handle array format for eventCalendar
            if (Array.isArray(eventCalendar)) {
              // Show event if any of its calendars match selected calendars
              return eventCalendar.some(cal => selectedCalendars.value.includes(cal));
            } else if (typeof eventCalendar === "string") {
              // Fallback for string format
              return selectedCalendars.value.includes(eventCalendar);
            }
            
            return false;
          });

          successCallback(filteredEvents);
        } else {
          successCallback([]);
        }
      })
      .catch(() => {
        isCalendarBusy.value = false;
        successCallback([]);
      });
  };

  const calendarOptions = ref({
    plugins: [dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin],
    initialView: "dayGridMonth",
    headerToolbar: {
      start: "sidebarToggle, prev,next, title",
      end: "dayGridMonth,timeGridWeek,listMonth",
    },
    events: fetchEvents,
    editable: false,
    eventResizableFromStart: false,
    dragScroll: false,
    dayMaxEventRows: 3,
    navLinks: true,
    eventClassNames({ event: calendarEvent }) {
      const calendarValue = calendarEvent.extendedProps.calendar;
      let colorName = "default";
      
      // Handle array format
      if (Array.isArray(calendarValue)) {
        if (calendarValue.includes("Staff") && calendarValue.includes("Students")) {
          colorName = "Both";
        } else if (calendarValue.includes("Students")) {
          colorName = "Students";
        } else if (calendarValue.includes("Staff")) {
          colorName = "Staff";
        }
      } else if (typeof calendarValue === "string") {
        colorName = calendarValue === 'Parents' ? 'Students' : calendarValue;
      }
      
      const getEventClasses = (type) => {
        switch (type) {
          case "Students":
            return ["fc-event-students"];
          case "Staff":
            return ["fc-event-staff"];
          case "Both":
            return ["fc-event-both"];
          default:
            return ["fc-event-default"];
        }
      };
      return getEventClasses(colorName);
    },
    eventClick({ event: clickedEvent }) {
      event.value = grabEventDataFromEventApi(clickedEvent);
      isEventHandlerSidebarActive.value = true;
    },
    customButtons: {
      sidebarToggle: {
        text: " ",
        click() {
          isCalendarOverlaySidebarActive.value =
            !isCalendarOverlaySidebarActive.value;
        },
      },
    },
    dateClick(info) {
      const formatDateOnly = (date) => {
        return date.toISOString().split("T")[0];
      };
      event.value = JSON.parse(
        JSON.stringify(
          Object.assign(event.value, {
            start: info.date,
            end: info.date,
          })
        )
      );
      isEventHandlerSidebarActive.value = true;
    },
    eventDrop({ event: droppedEvent }) {
      updateEvent(grabEventDataFromEventApi(droppedEvent));
    },
    eventResize({ event: resizedEvent }) {
      updateEvent(grabEventDataFromEventApi(resizedEvent));
    },
    direction: computed(() => (store.state.appConfig.isRTL ? "rtl" : "ltr")),
    rerenderDelay: 350,
  });

  const isEventHandlerSidebarActive = ref(false);
  const isCalendarOverlaySidebarActive = ref(false);

  return {
    refCalendar,
    isCalendarOverlaySidebarActive,
    calendarOptions,
    event,
    clearEventData,
    addEvent,
    updateEvent,
    removeEvent,
    refetchEvents,
    fetchEvents,
    isCalendarBusy,
    // ----- UI ----- //
    isEventHandlerSidebarActive,
  };
}
